import Router from 'next/router';
import * as Sentry from '@sentry/browser';

import contentGrouping from './contentGrouping';
import { getProduct, getOrders, getOrder } from '../pages/api';
import ssEventTrack from './sitespect';
import Queue from './queue';

const AMPLITUDE_QUEUE = new Queue();

const Amplitude = process.browser ? require('amplitude-js') : null;

const instance = Amplitude ? Amplitude.getInstance() : {
  init: () => {},
  logEvent: () => {},
  setUserId: () => {},
  setUserProperties: () => {}
};

const getFullProduct = (slug) => getProduct({ id: slug }).then((res) => res);

const getProductBadges = (product) => product?.trends?.map?.((t) => t.value);

const getProductCategory = (product) =>
  product?.breadcrumb_taxons?.[product?.breadcrumb_taxons?.length - 1]?.name;

const isSizeBroken = (product) => product?.variants?.reduce((sum, variant, idx, source) => {
  let temp = sum;
  if (variant?.total_on_hand === 0) temp += 1;
  if (source.length - 1 === idx) return temp > source.length / 2;
  return temp;
}, 0);

const getPageType = () => (Router.route === '/404'
  ? 'Page Not Found'
  : contentGrouping({ router: Router }));

const getOrderDiscounts = (cart) => {
  const lintItemPromotions = cart?.subtotals?.line_item_promotion_totals
      ?.reduce((a, b) => a + (+b.total || 0), 0) * -1;
  const miscPromotions = cart?.subtotals?.miscellaneous_adjustments
      ?.reduce((a, b) => a + (+b.amount || 0), 0) * -1;
  return lintItemPromotions + miscPromotions;
};

const getPaymentMethod = (type, method) => {
  switch (type) {
    case 'Spree::StoreCredit': return 'Store Credit';
    case 'CreditCard': return 'Credit Card';
    case 'PayPalAccount': return 'PayPal';
    case 'ApplePayCard': return 'Apple Pay';
    default: return method;
  }
};

const getPrice = (product) => {
  let price = product?.variants?.[0]?.price ?? product?.master?.price;

  if (product?.variants && !price) {
    for (let i = 1; i < product.variants.length; i++) {
      const variant = product.variants[i];
      if (variant.price) {
        price = variant.price;
        break;
      }
    }
  }

  return price ?? 0;
};

const resetUser = (reset_device_data = false) => {
  // Gets variables defined using SiteSpect WATTS method
  const activeTests = global?.window?.ss_campaign;

  instance.setUserId(null);
  if (reset_device_data) instance.regenerateDeviceId();
  instance.setUserProperties({
    'split tests': activeTests ? activeTests.split(',') : undefined,
    registered: false,
    purchases: null,
    'new customer': true,
    'last payment method': null
  });
};

const getPathNoQuery = () => {
  const { asPath } = Router;
  return asPath.split('?')[0];
};

const getQuery = () => {
  const { asPath } = Router;
  return asPath.split('?')[1];
};

const setUser = async (user) => {
  if (user?.id) {
    const { orders = [], total_count = 0 } = await getOrders({ page: 1 }).then((res) => res);

    const getLastPaymentUsed = async () => {
      if (total_count === 0) return null;
      const order = await getOrder({ id: orders?.[0]?.number }).then((res) => res);

      const paymentMethods = order?.payments
        ?.filter((payment) => payment?.state === 'completed' || payment?.state === 'pending')
        ?.map((payment) => {
          const method = payment?.payment_method?.name;
          if (method === 'Braintree') return payment?.source?.payment_type;
          return method;
        });

      return paymentMethods.length > 0 ? paymentMethods[0] : null;
    };

    // Gets variables defined using SiteSpect WATTS method
    const activeTests = global?.window?.ss_campaign;

    instance.setUserId(user.id);
    instance.setUserProperties({
      'split tests': activeTests ? activeTests.split(',') : undefined,
      registered: true,
      purchases: total_count,
      'new customer': total_count === 0,
      'last payment method': await getLastPaymentUsed()
    });
  } else {
    resetUser();
  }
};
const properties = {
  'Viewed Product Category Page': () => new Promise((resolve) => {
    const { query: { cms = false } } = Router;
    const category = cms && cms.replace('-home', '');
    resolve(category);
  }),
  'Viewed Product Description Page': ({ product }) => new Promise((resolve) => resolve({
    name: product?.name,
    inventory: product?.total_on_hand,
    productId: product?.id,
    price: getPrice(product),
    brand: product?.brand,
    category: getProductCategory(product),
    productSku: product?.master?.sku,
    badges: getProductBadges(product)
  })),
  'Viewed Checkout Page': ({ checkout }) => new Promise((resolve) => resolve({
    productSku: checkout?.line_items?.map((li) => li?.variant?.master_sku)
  })),
  'Viewed Product Listing Page': ({ result_meta }) => new Promise((resolve) => resolve({
    'number of results': result_meta?.total
  })),
  'Viewed Brand Page': ({ result_meta }) => new Promise((resolve) => resolve({
    'number of results': result_meta?.total
  })),
  'Viewed Search Page': ({ result_meta, searchTerm }) => new Promise((resolve) => resolve({
    'number of results': result_meta?.total,
    'search term': searchTerm
  })),
  'Removed From Cart': ({ product, cart }) => new Promise((resolve, reject) => getFullProduct(product?.variant?.slug)
    .then((fullProduct) => {
      resolve({
        name: product?.name,
        'size broken': isSizeBroken(fullProduct),
        position: product?.index,
        'parent page': getPathNoQuery(),
        productId: fullProduct?.id,
        price: product?.variant?.price,
        brand: product?.variant?.brand,
        category: getProductCategory(fullProduct),
        variantSku: product?.variant?.sku,
        badges: getProductBadges(fullProduct),
        image: product?.variant?.images?.[0]?.product_url,
        $quantity: product?.quantity,
        productSku: fullProduct?.master?.sku,
        'page type': getPageType(),
        'total cart value': cart?.total,
        'total cart size': cart?.total_quantity,
        'cart id': cart?.number
      });
    })
    .catch((err) => reject(err))),
  'Added To Cart': ({
    product, cart, variant, quantity, position
  }) => new Promise((resolve) => resolve({
    name: product?.name,
    'size broken': isSizeBroken(product),
    position,
    'parent page': getPathNoQuery(),
    productId: product?.id,
    price: variant?.price,
    brand: product?.brand,
    category: getProductCategory(product),
    variantSku: variant?.sku,
    badges: getProductBadges(product),
    image: variant?.images?.[0]?.product_url,
    $quantity: quantity,
    productSku: product?.master?.sku,
    'page type': getPageType(),
    'total cart value': cart?.total,
    'total cart size': cart?.total_quantity,
    'cart id': cart?.number,
    'vendor id': variant?.prices?.[0]?.vendor_id
  })),
  'Clicked Button In Cart': ({ buttonName }) => new Promise((resolve) => resolve({
    'button clicked': buttonName
  })),
  'Clicked Product Card': ({ product, module, index }) => new Promise((resolve, reject) => getFullProduct(product?.product_slug)
    .then((fullProduct) => {
      resolve({
        name: product?.title,
        'size broken': isSizeBroken(fullProduct),
        'position type': module,
        position: index,
        'parent page': getPathNoQuery(),
        productId: fullProduct?.id,
        brand: fullProduct?.brand,
        category: product?.main_category,
        productSku: fullProduct?.master?.sku,
        badges: getProductBadges(fullProduct),
        image: fullProduct?.master?.images?.[0]?.product_url,
        maisonetteSku: product?.maisonette_sku,
        'page type': getPageType()
      });
    })
    .catch((err) => reject(err))),
  'Clicked Product Quick View': ({ product, index, module }) => new Promise((resolve, reject) => getFullProduct(product?.product_slug)
    .then((fullProduct) => {
      resolve({
        name: product?.title,
        'size broken': isSizeBroken(fullProduct),
        'position type': module,
        position: index,
        'parent page': getPathNoQuery(),
        productId: product?.maisonette_product_id,
        brand: product?.brand,
        category: product?.main_category,
        productSku: fullProduct?.master?.sku,
        badges: product?.badges_by_trends?.split('^')?.filter((x) => x),
        image: product?.image,
        maisonetteSku: product?.maisonette_sku,
        'page type': getPageType()
      });
    })
    .catch((err) => reject(err))),
  'Opened Search': () => new Promise((resolve) => resolve({
    'parent page': getPathNoQuery(),
    'page type': getPageType()
  })),
  'Viewed Search Results': ({ totalChars, query, totalResults }) => new Promise((resolve) => resolve({
    'number of characters': totalChars,
    'search term': query,
    'number of results returned': totalResults
  })),
  'Clicked Search Results View All': ({ totalResults, elementClicked }) => new Promise((resolve) => resolve({
    'number of results returned': totalResults,
    'element clicked': elementClicked
  })),
  'Used Search Suggestion': ({ term }) => new Promise((resolve) => resolve({
    'suggested search term': term
  })),
  'Used Popular Search Suggestion': ({ term }) => new Promise((resolve) => resolve({
    'popular search term': term
  })),
  'Used Recent Search Suggestion': ({ term }) => new Promise((resolve) => resolve({
    'recent search term': term
  })),
  'Clicked Search Product Recommendation': ({ productSku }) => new Promise((resolve) => resolve({ productSku })),
  'Clicked Search Content Recommendation': ({ url, title }) => new Promise((resolve) => resolve({ url, title })),
  'Started Checkout': ({ cart }) => new Promise((resolve) => resolve({
    'cart id': cart?.number,
    'total cart value': cart?.total,
    'total cart size': cart?.total_quantity,
    'parent page': getPathNoQuery(),
    productSku: cart?.line_items?.map((li) => li?.variant?.master_sku)
  })),
  'Clicked Content Module': ({
    title, text, position, link, img
  }) => new Promise((resolve) => resolve({
    'module name': title,
    'module text': text,
    img,
    link,
    'parent page': getPathNoQuery(),
    position: position + 1,
    'page type': getPageType()
  })),
  'Shown Address Verification Modal': ({ cartId, modalType, errorCode }) => new Promise((resolve) => resolve({
    'cart id': cartId,
    'modal type': modalType,
    'error type': errorCode
  })),
  'Submitted Address Verification Modal': ({ cartId, modalType, buttonType }) => new Promise((resolve) => resolve({
    'cart id': cartId,
    'modal type': modalType,
    'CTA used': buttonType
  })),
  'Clicked Checkout Edit': ({ cartId, fromStep, toStep }) => new Promise((resolve) => resolve({
    'cart id': cartId,
    'from step': fromStep,
    'to step': toStep
  })),
  'Clicked Checkout Cart Edit': ({ cartId, cartState }) => new Promise((resolve) => resolve({
    'cart id': cartId,
    'cart state': cartState
  })),
  'Submitted Checkout Address': ({ cart }) => new Promise((resolve) => resolve({
    'cart id': cart?.number,
    'total cart value': cart?.total,
    'total cart size': cart?.total_quantity,
    productSku: cart?.line_items?.map((li) => li?.variant?.master_sku)
  })),
  'Submitted Checkout Shipping': ({ cart }) => new Promise((resolve) => resolve({
    'cart id': cart?.number,
    'total cart value': cart?.total,
    'total cart size': cart?.total_quantity,
    'shipping method': cart?.shipments?.map((s) => s?.selected_shipping_rate?.name),
    productSku: cart?.line_items?.map((li) => li?.variant?.master_sku)
  })),
  'Submitted Checkout Payment': ({ cart, method = null, types = null }) => new Promise((resolve) => resolve({
    'cart id': cart?.number,
    'total cart value': cart?.total,
    'total cart size': cart?.total_quantity,
    'payment method': (() => {
      if (method) return [getPaymentMethod(null, method)];
      if (Array.isArray(types)) return types.map((x) => getPaymentMethod(x));
      return [getPaymentMethod(types)];
    })(),
    productSku: cart?.line_items?.map((li) => li?.variant?.master_sku)
  })),
  'Submitted Checkout Order': ({ cart }) => new Promise((resolve) => resolve({
    'cart id': cart?.number,
    'total cart value': cart?.total,
    'total cart size': cart?.total_quantity,
    'promo code applied': cart?.applied_promotion_codes.map((p) => p?.value) ?? null,
    'order total': cart?.total,
    'payment method': cart?.payments
        ?.filter((p) => p.state === 'checkout')
        ?.map((p) => getPaymentMethod(p.source.payment_type || p?.source_type)),
    'shipping method': cart?.shipments?.map((s) => s?.selected_shipping_rate?.name),
    'total discounts': getOrderDiscounts(cart),
    'total shipping': cart?.subtotals?.shipments_total,
    'total tax': cart?.tax_total,
    variantSku: cart?.line_items?.map((li) => li?.variant?.sku),
    productSku: cart?.line_items?.map((li) => li?.variant?.master_sku)
  })),
  'Clicked Facet': ({ navigationItem, selected, facets }) => new Promise((resolve) => resolve({
    'navigation item': navigationItem,
    selected,
    facets
  })),
  'Navigated Site': ({
    navigationTerm,
    path,
    navigationType,
    navBreadcrumb
  }) => new Promise((resolve) => resolve({
    'navigation term': navigationTerm,
    'category path': path,
    'navigation type': navigationType,
    'parent page': getPathNoQuery(),
    'page type': getPageType(),
    breadcrumb: navBreadcrumb
  })),
  'Confirmed Purchase': ({ cart, user }) => new Promise((resolve) => {
    // TODO: what to do with guest user?
    if (user) setUser(user);

    return resolve({
      'first order': cart?.first_order,
      'transaction id': cart?.id,
      'transaction total': cart?.total,
      'transaction tax': cart?.tax_total,
      'transaction shipping': cart?.subtotals?.shipments_total,
      'transaction currency': cart?.currency,
      'cart id': cart?.number,
      discount: getOrderDiscounts(cart),
      coupon: cart?.applied_promotion_codes.map((p) => p?.value) ?? null,
      productId: cart?.line_items?.map((li) => li?.variant?.product_id),
      productSku: cart?.line_items?.map((li) => li?.variant?.master_sku),
      name: cart?.line_items?.map((li) => li?.variant?.name),
      'product data array': cart?.line_items?.map((li, i) => ({
        name: li?.variant?.name,
        'size broken': isSizeBroken(li),
        'position type': module,
        position: i + 1,
        'parent page': getPathNoQuery(),
        productId: li?.variant?.product_id,
        brand: li?.variant?.brand,
        image: li?.images?.[0]?.product_url,
        variantSku: li?.variant?.sku,
        'page type': getPageType()
      }))
    });
  }),
  'Encountered Transaction Error': ({ message, step }) => new Promise((resolve) => resolve({
    'error type': message,
    'checkout step': step
  })),
  'Cleared All Filters': ({ facets }) => new Promise((resolve) => resolve({ facets })),
  'Submitted Promo Code': ({ code, removed_promo }) => new Promise((resolve) => resolve({ code, removed_promo })),
  'Submitted Gift Message': ({ removed_gift_message }) => new Promise((resolve) => resolve({ removed_gift_message }))
};

const logAmplitude = async (eventName, payload = {}) => {
  try {
    const { router } = Router;

    let groupEventName;
    // if no event name given, use content grouping to set event name
    // for page view events that are grouped by page type
    if (!eventName) {
      const contentGroup = contentGrouping({ router, pageProps: payload });
      groupEventName = `Viewed ${contentGroup?.replace('Pages', 'Page')}`;
    }

    AMPLITUDE_QUEUE
      .enqueue(() => (properties[`${eventName || groupEventName}`]?.(payload)))
      .then((props) => {
        instance.logEvent((eventName || groupEventName), { ...(groupEventName && { 'parent page': getPathNoQuery(), query: getQuery() }), ...(props && props) });
        ssEventTrack(eventName || groupEventName, { ...(groupEventName && { 'parent page': getPathNoQuery(), query: getQuery() }), ...(props && props) });
      });
  } catch (error) {
    Sentry.withScope(() => {
      Sentry.captureException(error);
      // ... anything else ...
    });
  }
};

const logRevenueAmplitude = ({ lineItem, orderNumber }) => {
  try {
    const { variant } = lineItem;

    const eventProperties = {
      variantSku: variant?.sku,
      name: variant?.name,
      category: getProductCategory(lineItem),
      'transaction id': lineItem?.cart?.id,
      'cart id': orderNumber,
      productId: lineItem?.variant?.product_id,
      productSku: lineItem?.variant?.master_sku
    };

    const revenue = new instance.Revenue()
      .setProductId(`${variant?.product_id}`)
      .setQuantity(+lineItem?.quantity)
      .setPrice(+lineItem?.price)
      .setRevenueType('purchase')
      .setEventProperties(eventProperties);

    instance.logRevenueV2(revenue);
    ssEventTrack('Revenue', revenue);
  } catch (error) {
    Sentry.withScope(() => {
      // handle errors for amplitude here.
      Sentry.captureException(error);
      // ... anything else ...
    });
  }
};

export {
  setUser, resetUser, logAmplitude, logRevenueAmplitude
};

export default instance;
